import { Receipt } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Badge,
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DataTable,
  EmptyState,
  toast,
} from '@/components/ui'
import { translateEnumValue } from '@/lib/formatting'

import type { BillingOverview, Invoice } from '../billingApi'
import { formatShortDate } from '../format'
import { useCancelSubscription, useInvoice, useInvoices } from '../queries'
import { useBillingErrorText } from '../billingErrors'
import { ProfileDialog } from './PlansSection'

function useMoney(currency: string) {
  const { i18n } = useTranslation()
  return (value: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency }).format(value)
}

function statusVariant(status: string) {
  return status === 'Paid'
    ? 'success'
    : status === 'Overdue'
      ? 'danger'
      : status === 'Issued' || status === 'Issuing'
        ? 'warning'
        : 'neutral'
}

/** Fatura em aberto: "Pagar agora" (página hospedada) + Pix copia-e-cola quando o provedor oferece. */
export function OpenInvoiceCard({
  tenantId,
  invoice,
}: {
  readonly tenantId: string
  readonly invoice: Invoice
}) {
  const { t, i18n } = useTranslation()
  const money = useMoney(invoice.currency)
  const detail = useInvoice(tenantId, invoice.id)
  const copyPaste = detail.data?.pix?.copyPaste ?? null

  return (
    <Card data-testid="open-invoice">
      <CardHeader>
        <CardTitle>{t('billing.invoice.title')}</CardTitle>
        <CardDescription>
          {t('billing.invoice.number', { number: invoice.number })} · {money(invoice.amount)} ·{' '}
          {t('billing.invoice.due', {
            date: formatShortDate(`${invoice.dueDate}T12:00:00Z`, i18n.language),
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {invoice.paymentUrl === null ? (
          <Alert variant="info" title={t('billing.invoice.issuing')} />
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={invoice.paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ variant: 'primary', size: 'sm' })}
            >
              {t('billing.invoice.payNow')}
            </a>
            {copyPaste !== null ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void navigator.clipboard
                    .writeText(copyPaste)
                    .then(() => {
                      toast.success(t('billing.invoice.copied'))
                    })
                    .catch(() => undefined)
                }}
              >
                {t('billing.invoice.copy')}
              </Button>
            ) : null}
          </div>
        )}
        <p className="text-foreground-muted">{t('billing.invoice.awaitingActivation')}</p>
      </CardContent>
    </Card>
  )
}

export function ProfileSection({
  tenantId,
  overview,
}: {
  readonly tenantId: string
  readonly overview: BillingOverview
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const profile = overview.profile

  return (
    <Card data-testid="billing-profile">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{t('billing.profile.title')}</CardTitle>
          {overview.canManage ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setOpen(true)
              }}
            >
              {t('billing.profile.edit')}
            </Button>
          ) : null}
        </div>
        <CardDescription>{t('billing.profile.description')}</CardDescription>
      </CardHeader>
      <CardContent className="text-sm">
        {profile === null ? (
          <p className="text-foreground-muted">{t('billing.profile.empty')}</p>
        ) : (
          <dl className="grid gap-1 sm:grid-cols-[auto_1fr] sm:gap-x-4">
            <dt className="text-foreground-muted">{t('billing.profile.legalName')}</dt>
            <dd className="text-foreground-primary">{profile.legalName}</dd>
            <dt className="text-foreground-muted">{t('billing.profile.taxId')}</dt>
            <dd className="text-foreground-primary" data-testid="tax-id-masked">
              {profile.taxIdMasked}
            </dd>
            <dt className="text-foreground-muted">{t('billing.profile.email')}</dt>
            <dd className="text-foreground-primary">{profile.email}</dd>
          </dl>
        )}
      </CardContent>
      {open ? (
        <ProfileDialog
          tenantId={tenantId}
          open
          current={profile}
          submitLabel={t('billing.profile.save')}
          onClose={() => {
            setOpen(false)
          }}
          onSaved={() => {
            setOpen(false)
          }}
        />
      ) : null}
    </Card>
  )
}

export function InvoicesSection({
  tenantId,
  currency,
}: {
  readonly tenantId: string
  readonly currency: string
}) {
  const { t, i18n } = useTranslation()
  const money = useMoney(currency)
  const invoices = useInvoices(tenantId, true)
  const statuses = t('billing.invoices.statuses', { returnObjects: true }) as Record<string, string>
  const rows = invoices.data?.items ?? []

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-foreground-primary">
        {t('billing.invoices.title')}
      </h2>
      {invoices.isSuccess && rows.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title={t('billing.invoices.empty')}
          description={t('billing.invoices.emptyDescription')}
        />
      ) : (
        <DataTable
          caption={t('billing.invoices.title')}
          rows={rows}
          rowKey={(row) => row.id}
          columns={[
            {
              key: 'number',
              header: t('billing.invoices.columns.number'),
              cell: (row) => row.number,
            },
            {
              key: 'date',
              header: t('billing.invoices.columns.date'),
              cell: (row) => formatShortDate(row.createdAt, i18n.language),
            },
            {
              key: 'amount',
              header: t('billing.invoices.columns.amount'),
              cell: (row) => money(row.amount),
              align: 'right',
            },
            {
              key: 'status',
              header: t('billing.invoices.columns.status'),
              cell: (row) => (
                <Badge variant={statusVariant(row.status)}>
                  {translateEnumValue(statuses, row.status)}
                </Badge>
              ),
            },
            {
              key: 'action',
              header: t('billing.invoices.columns.action'),
              headerSrOnly: true,
              cell: (row) =>
                (row.status === 'Issued' || row.status === 'Overdue') && row.paymentUrl !== null ? (
                  <a
                    href={row.paymentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-action-primary hover:underline"
                  >
                    {t('billing.invoices.pay')}
                  </a>
                ) : null,
            },
          ]}
        />
      )}
    </section>
  )
}

export function CancelSection({
  tenantId,
  overview,
}: {
  readonly tenantId: string
  readonly overview: BillingOverview
}) {
  const { t, i18n } = useTranslation()
  const errorText = useBillingErrorText()
  const cancel = useCancelSubscription(tenantId)
  const [confirming, setConfirming] = useState(false)
  const subscription = overview.subscription

  if (subscription === null || !overview.canManage) {
    return null
  }

  if (subscription.cancelAtPeriodEnd) {
    return (
      <Alert
        variant="info"
        title={t('billing.subscription.cancelScheduled', {
          date:
            subscription.currentPeriodEnd === null
              ? ''
              : formatShortDate(subscription.currentPeriodEnd, i18n.language),
        })}
      />
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('billing.cancel.title')}</CardTitle>
        <CardDescription>{t('billing.cancel.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setConfirming(true)
          }}
        >
          {t('billing.cancel.action')}
        </Button>
      </CardContent>
      <ConfirmDialog
        open={confirming}
        title={t('billing.cancel.confirmTitle')}
        description={t('billing.cancel.confirmDescription')}
        confirmLabel={t('billing.cancel.confirm')}
        cancelLabel={t('billing.cancel.keep')}
        variant="danger"
        loading={cancel.isPending}
        onCancel={() => {
          setConfirming(false)
        }}
        onConfirm={() => {
          cancel.mutate(undefined, {
            onSuccess: () => {
              setConfirming(false)
              toast.success(t('billing.cancel.done'))
            },
            onError: (error) => {
              setConfirming(false)
              toast.error(errorText(error))
            },
          })
        }}
      />
    </Card>
  )
}
